import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth.ts';
import { useSeo } from '../hooks/useSeo.ts';

/**
 * Forgot-password kick-off. Always shows the same "if an account exists, we sent a link"
 * message after submit — the backend doesn't tell us whether the email was real, and we
 * don't want to either (enumeration defense).
 */
export default function ForgotPasswordPage() {
  useSeo({ title: 'Reset your password', canonicalPath: '/forgot-password' });

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const request = useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
    onSettled: () => {
      // Regardless of success/failure, show the same confirmation — the API itself doesn't
      // leak the truth, but we should mirror that on the client side too.
      setSubmitted(true);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    request.mutate(email.trim());
  };

  if (submitted) {
    return (
      <div className="sign-in">
        <h1>Check your email</h1>
        <p className="muted">
          If an account exists for <strong>{email}</strong>, we've sent a password-reset link.
          It expires in 1 hour.
        </p>
        <p className="muted small">
          Still nothing after a minute? Check spam, or{' '}
          <button type="button" className="link-button" onClick={() => { setSubmitted(false); request.reset(); }}>
            try a different email
          </button>.
        </p>
        <Link to="/sign-in" className="cta">Back to sign-in</Link>
      </div>
    );
  }

  return (
    <div className="sign-in">
      <h1>Reset your password</h1>
      <p className="muted">
        Enter the email you signed up with. We'll send a link to choose a new password.
      </p>

      <form className="auth-form" onSubmit={onSubmit}>
        <label>
          <span>Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            maxLength={256}
          />
        </label>

        <button type="submit" className="cta" disabled={request.isPending}>
          {request.isPending ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <p className="muted small auth-toggle">
        Remembered it? <Link to="/sign-in">Sign in</Link>
      </p>
    </div>
  );
}
